import { Component, ReactNode } from 'react';
import { en, fa } from '@/i18n';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI. Defaults to the built-in card. */
  fallback?: ReactNode;
  /** Called after the user clicks "Try Again" and state is reset. */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ─── Default Fallback UI ──────────────────────────────────────────────────────
// Reads document language so copy still switches even if LanguageProvider crashed.

function DefaultFallback({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) {
  const copy = (typeof document !== 'undefined' && document.documentElement.lang === 'fa')
    ? fa.errorBoundary
    : en.errorBoundary;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card text-card-foreground shadow-lg p-8 text-center">
        <div className="text-5xl mb-4" role="img" aria-label={copy.sadFace}>
          😞
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {copy.title}
        </h1>
        <p className="text-muted-foreground text-sm mb-6">
          {copy.body}
        </p>

        {error && (
          <details className="text-left mb-6 rounded-lg bg-muted p-3 text-xs text-muted-foreground overflow-auto max-h-32">
            <summary className="cursor-pointer font-medium mb-1">
              {copy.details}
            </summary>
            <pre className="whitespace-pre-wrap break-all mt-2">
              {error.message}
            </pre>
          </details>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={onReset}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {copy.tryAgain}
          </button>
          {/* Plain <a> — not React Router Link — so it works even if the router crashed */}
          <a
            href="/"
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            {copy.goHome}
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── ErrorBoundary ────────────────────────────────────────────────────────────

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Intentional console.error — this is a genuine runtime error, not a debug log.
    // In production this is where you'd forward to an error reporting service.
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <DefaultFallback error={this.state.error} onReset={this.handleReset} />
      );
    }
    return this.props.children;
  }
}

// ─── RouteErrorBoundary ───────────────────────────────────────────────────────
// Thin wrapper used around <Routes>. On reset it reloads the page to guarantee
// a clean JS state — the right recovery action for a crashed page component.

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      {children}
    </ErrorBoundary>
  );
}
